// ==================== LEAD GEN PAGE WRAPPER ====================

import { Suspense, type ReactNode } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  KanbanSquare,
  Mail,
  Calendar,
  BarChart2,
  SlidersHorizontal,
  Bot,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { ROUTES } from '../../../constants';
import { useAuth } from '../../../contexts';
import LeadGenRunStatus from './LeadGenRunStatus';
import { LeadGenRunProvider } from './LeadGenRunContext';

// ==================== ROUTE META MAP ====================
const routeMeta: Record<string, { label: string; icon: React.ElementType; description: string }> = {
  [ROUTES.CUSTOMER.LEAD_GEN_DASHBOARD]: {
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Real-time overview of your AI-powered lead generation activity.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_DISCOVERY]: {
    label: 'Lead Discovery',
    icon: Target,
    description: 'Discover and score new companies matching your ideal customer profile.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_PIPELINE]: {
    label: 'Lead Pipeline',
    icon: KanbanSquare,
    description: 'Track leads across funnel stages — from discovery to proposal.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_OUTREACH]: {
    label: 'Outreach Campaigns',
    icon: Mail,
    description: 'Automate and track AI-personalized outreach emails to your qualified leads.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_MEETINGS]: {
    label: 'Meetings Booked',
    icon: Calendar,
    description: 'Confirmed meetings with upcoming briefs and preparation tools.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_REPORTS]: {
    label: 'Performance Reports',
    icon: BarChart2,
    description: 'Funnel, outreach and meeting analytics across all your campaigns.',
  },
  [ROUTES.CUSTOMER.LEAD_GEN_SETTINGS]: {
    label: 'Agent Settings',
    icon: SlidersHorizontal,
    description: 'Campaign defaults (ICP & scoring), your sending email, and agent behaviour.',
  },
};

// ==================== AGENT STATUS PILL ====================
function AgentStatus() {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-(--color-success-light) border border-(--color-success)/25 text-xs font-medium text-(--color-success)">
      <span className="h-1.5 w-1.5 rounded-full bg-(--color-success) animate-pulse" />
      Agent Active
    </div>
  );
}

// ==================== LAZY CONTENT FALLBACK ====================
function PageContentFallback() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-(--color-muted-gray)">
      <Loader2 className="h-7 w-7 animate-spin text-(--color-signal-orange)" />
      <p className="text-sm">Loading…</p>
    </div>
  );
}

// ==================== WRAPPER COMPONENT ====================
interface LeadGenPageWrapperProps {
  children: ReactNode;
}

export default function LeadGenPageWrapper({ children }: LeadGenPageWrapperProps) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const meta = routeMeta[pathname];

  // Lead Generation is a paid recurring add-on. If it isn't enabled for this
  // customer's subscription, they can't access these pages by URL - send them
  // to Billing where they can enable it.
  if (user?.organization?.subscription?.leadGenAddonEnabled !== true) {
    return <Navigate to={ROUTES.CUSTOMER.SETTINGS_BILLING} replace />;
  }

  if (!meta) {
    return (
      <LeadGenRunProvider>
        <div className="p-6">
          <Suspense fallback={<PageContentFallback />}>{children}</Suspense>
        </div>
      </LeadGenRunProvider>
    );
  }

  const Icon = meta.icon;

  return (
    <LeadGenRunProvider>
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-(--color-muted-gray) mb-3">
            <Bot className="h-3.5 w-3.5" />
            <span>Lead Generation</span>
            {meta.label !== 'Dashboard' && (
              <>
                <ChevronRight className="h-3 w-3" />
                <span className="text-white">{meta.label}</span>
              </>
            )}
          </div>

          {/* Title row */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-(--color-signal-orange)/10 border border-(--color-signal-orange)/20 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-(--color-signal-orange)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white">{meta.label}</h1>
              <p className="text-sm text-(--color-muted-gray) mt-0.5">{meta.description}</p>
            </div>
          </div>
        </div>

        <AgentStatus />
      </div>

      {/* Live pipeline run status (real-time, all lead-gen pages) */}
      <LeadGenRunStatus />

      {/* Page Content (lazy-loaded) */}
      <Suspense fallback={<PageContentFallback />}>{children}</Suspense>
    </div>
    </LeadGenRunProvider>
  );
}
